import React from "react";
import { getAllUsedPublications } from "@plugins/gatsby-source-paligo/src/config";
import { PublicationsConfigEntry } from "@plugins/gatsby-source-paligo/src/types";
import { useProductVersion } from "contexts/ProductVersionContext";
import { useSiteContext } from "contexts/SiteContext";
import { navigate } from "gatsby";
import { use404ModalOpener } from "hooks/use-404-modal-opener";

import { VersionSelector } from "components/VersionSelector/VersionSelector";

import { SELECT_VERSION_REGEX } from "utils/regex";
import { formatDocumentPath, parseDocumentPath, PathObject } from "utils/url";
import { getVersionSelectorValueFromPath } from "utils/version-selector";

import "./NavVersionSelector.scss";

const createArchivedUrl = (archivedVersion: string) =>
    `https://archived.docs.singlestore.com/v${archivedVersion}`;

const publications = getAllUsedPublications();

const archivedVersionsUrls = Object.fromEntries(
    publications
        .flatMap((publicationConfig) => publicationConfig.archivedVersions)
        .map((archivedVersion) => [
            `v${archivedVersion}`,
            createArchivedUrl(archivedVersion),
        ])
);

export function NavVersionSelector() {
    const { pathname } = useSiteContext();
    const open404Modal = use404ModalOpener();
    const pathInfo = parseDocumentPath(pathname);
    const { versionToUrl } = useProductVersion();

    const [loading, setLoading] = React.useState<boolean>(false);

    React.useEffect(() => {
        return () => {
            setLoading(false);
        };
    }, []);

    const onVersionChange = (value: string) => {
        setLoading(true);

        const match = value.match(SELECT_VERSION_REGEX) as RegExpMatchArray;

        if (!match) {
            console.error("Unrecognised product or version");
            return;
        }

        const product = match[1];
        const version = match[2];

        if (version in archivedVersionsUrls) {
            window.open(archivedVersionsUrls[version]);
            setLoading(false);
            return;
        }

        const publicationConfig = publications.find(
            (config) => config.slug === product
        ) as PublicationsConfigEntry;
        const isVersioned = Boolean(publicationConfig.pastVersions.length > 0);

        let newPathInfo: PathObject = {
            ...pathInfo,
            product: product,
            version: isVersioned ? version : undefined,
        };

        // if there's no document info, we're on a landing page
        const isLandingPage = newPathInfo.documentPath.length === 0;
        if (isLandingPage) {
            let productSlug: Maybe<string> = product;
            let versionSlug: Maybe<string> = version;

            if (publicationConfig.defaultPublication) {
                productSlug = undefined;
                versionSlug = undefined;
            }

            newPathInfo = {
                documentPath: [],
                locale: undefined,
                version: versionSlug,
                product: productSlug,
            };
        }

        const expectedPath = formatDocumentPath(newPathInfo);
        const documentPath = versionToUrl?.[value];

        if (isLandingPage) {
            navigate(expectedPath);
        } else if (documentPath) {
            navigate(documentPath);
        } else {
            open404Modal(expectedPath);
        }
        return;
    };

    const currentValue = getVersionSelectorValueFromPath({ pathInfo });

    return (
        <VersionSelector
            includeArchivedVersions
            value={currentValue}
            loading={loading}
            contentClassName="nav-version-selector-content"
            onValueChange={onVersionChange}
        />
    );
}
